import { useState, type FormEvent } from 'react'
import { ApiError } from '../../lib/api.ts'
import { useLogin } from '../../hooks/useSession.ts'
import styles from './LoginView.module.css'

/**
 * docs/ui-spec.md §5.1. No registration, no password recovery, no marketing split — the screen
 * says so out loud, because the absence is deliberate (ADR 0007).
 */
export function LoginView(): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const login = useLogin()

  const message = errorMessage(login.error)
  const lockedUntil = lockedUntilFrom(login.error)
  const locked = lockedUntil !== null && lockedUntil > Date.now()

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault()
    if (locked || login.isPending) return
    login.mutate({ email, password })
  }

  return (
    <main className={styles.screen}>
      <div className={styles.column}>
        <div className={styles.brand}>
          <span className={styles.mark} aria-hidden="true" />
          <span className={styles.brandName}>ensynio tracker</span>
        </div>

        <form className={styles.card} onSubmit={onSubmit} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              E-mail
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              autoComplete="username"
              // Jedyne pole na stronie, która nie robi nic innego — nie ma czego przeskoczyć.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={email}
              disabled={login.isPending || locked}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              Hasło
            </label>
            <input
              id="password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={login.isPending || locked}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {/* Fixed height: the form must not jump when a message appears. */}
          <div className={styles.errorSlot} role="alert" aria-live="polite">
            {locked ? lockedMessage(lockedUntil) : message}
          </div>

          <button className={styles.submit} type="submit" disabled={login.isPending || locked}>
            {login.isPending ? 'Logowanie…' : 'Zaloguj'}
          </button>
        </form>

        <p className={styles.footnote}>
          Konta zakłada administrator z terminala VPS.
          <br />
          Brak rejestracji i odzyskiwania hasła.
        </p>
      </div>
    </main>
  )
}

function errorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return error ? 'Nie udało się połączyć z serwerem.' : ''
  if (error.body.error === 'rate_limited') return ''
  if (error.body.error !== 'invalid_credentials') return 'Nie udało się zalogować.'

  const left = error.body.attemptsLeft
  const suffix =
    left === undefined ? '' : ` ${left === 1 ? 'Została 1 próba.' : `Zostały ${left} próby.`}`
  return `Nieprawidłowy e-mail lub hasło.${suffix}`
}

function lockedUntilFrom(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.body.error !== 'rate_limited') return null
  return error.body.retryAt ?? null
}

function lockedMessage(retryAt: number | null): string {
  if (retryAt === null) return 'Za dużo prób. Spróbuj później.'
  const minutes = Math.max(1, Math.ceil((retryAt - Date.now()) / 60_000))
  return `Za dużo prób. Spróbuj za ${minutes} min.`
}
